import { io } from 'socket.io-client';
import { apiClient } from '@/api';
import { ElNotification } from 'element-plus';
import { h } from 'vue';
import { eventBus } from '@/components/util/eventBus'
import { getCookieValue } from '@/components/util/cookies'

const QUEUE_LIMIT = 10;
const QUEUE_TTL_MS = 30_000; // 30s
const ALLOWED_QUEUE_EVENTS = new Set(['check-wa-auth', 'whatsapp-start-auth', 'whatsapp:clear-session']);

function decodeJwt(token) {
    try {
        const payload = token.split('.')[1];
        const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
        return JSON.parse(decoded);
    } catch (e) {
        console.error('Failed to decode JWT:', e);
        return null;
    }
}

export default {
    namespaced: true,
    state: () => ({
        socket: null,
        connected: false,
        connectionStatus: 'disconnected',
        wasLoggedOut: false,
        profiles: [],
        qrCode: null, // используем один QR код для одного messenger_id
        currentMessengerId: null, // для отслеживания активного процесса авторизации
        whatsappReadyStatuses: {}, // { messenger_id: true/false }
        messages: [],
        notifications: [],
        whatsappNumber: null,
        selectedDialog: null,
        waAuthCheckInProgress: false,
        pendingEmits: [],
    }),
    mutations: {
        CLEAR_SELECTED_DIALOG(state) {
            state.selectedDialog = null;
        },
        SET_SELECTED_DIALOG(state, dialog) {
            state.selectedDialog = dialog;
            if (dialog?.messenger_id) {
                this.dispatch('socket/send', {
                    event: 'check-wa-auth',
                    data: { dialog_id: dialog.id }
                });
            }
        },
        SET_SOCKET(state, socket) {
            state.socket = socket;
        },
        SET_CONNECTED(state, status) {
            state.connected = status;
        },
        SET_CONNECTION_STATUS(state, status) {
            state.connectionStatus = status;
        },
        SET_WAS_LOGGED_OUT(state, status) {
            state.wasLoggedOut = status;
        },
        SET_PROFILES(state, profiles) {
            state.profiles = profiles;
        },
        SET_QR_CODE(state, qr) {
            state.qrCode = qr;
        },
        CLEAR_QR_CODE(state) {
            state.qrCode = null;
        },
        SET_CURRENT_MESSENGER_ID(state, id) {
            state.currentMessengerId = id;
        },
        SET_WHATSAPP_READY(state, { messenger_id, status }) {
            state.whatsappReadyStatuses = { ...state.whatsappReadyStatuses, [messenger_id]: status };
        },
        ADD_NOTIFICATION(state, note) {
            state.notifications.push(note);
        },
        SET_WHATSAPP_NUMBER(state, { messenger_id, number, pushname }) {
            state.whatsappNumber = { messenger_id, number, pushname };
        },
        SET_WA_AUTH_CHECK_IN_PROGRESS(state, status) {
            state.waAuthCheckInProgress = status;
        },
        PUSH_PENDING_EMIT(state, emit) {
            state.pendingEmits.push(emit);
        },
        CLEAR_PENDING_EMITS(state) {
            state.pendingEmits = [];
        },
    },
    actions: {
        connect({ state, commit }) {
            const token = getCookieValue('token');
            if (state.socket || !token) return;
            const decoded = decodeJwt(token);
            const accountId = decoded.account_id;
            const userId = decoded.user_id;

            commit('SET_CONNECTION_STATUS', 'connecting');

            const socket = io('https://ws.lineon.me:3134', {
                path: '/socket.io',           // должен совпадать с сервером
                withCredentials: true,
                transports: ['websocket'],
                // НЕ форсируем только websocket — оставляем polling+upgrade, так надёжнее за TLS/прокси
                auth: { token },
            });

            commit('SET_SOCKET', socket);

            const flushQueuedEmits = () => {
                const now = Date.now();
                const queue = state.pendingEmits.filter(e => (now - e.timestamp) < QUEUE_TTL_MS).slice();
                if (!queue.length) return;
                queue.forEach(({ event, data, callback }) => {
                    if (typeof callback === 'function') {
                        socket.emit(event, data, callback);
                    } else {
                        socket.emit(event, data);
                    }
                });
                commit('CLEAR_PENDING_EMITS');
            };

            socket.on('connect', () => {
                commit('SET_CONNECTED', true);
                commit('SET_CONNECTION_STATUS', 'connected');
                commit('SET_WAS_LOGGED_OUT', false);
                console.log('Socket connected');
                flushQueuedEmits();
            });

            socket.on('disconnect', () => {
                commit('SET_CONNECTED', false);
                commit('SET_CONNECTION_STATUS', 'disconnected');
                console.log('Socket disconnected');
            });

            socket.on('whatsapp-profiles', (profiles) => {
                console.log('Получены профили WhatsApp:', profiles);
                commit('SET_PROFILES', profiles);
            });

            socket.on('whatsapp-qr', ({ messenger_id, code }) => {
                console.log('ПОЛУЧЕН QR-КОД:', { messenger_id, code });
                console.log('Текущий ожидаемый messenger_id:', state.currentMessengerId);
                if (state.currentMessengerId === messenger_id) {
                    commit('SET_QR_CODE', code);
                } else {
                    console.log(`QR получен для messenger_id=${messenger_id}, но игнорируется, ожидается ${state.currentMessengerId}`);
                }
            });

            socket.on('whatsapp-ready', ({ messenger_id }) => {
                commit('SET_WHATSAPP_READY', { messenger_id, status: true });
                if (state.currentMessengerId === messenger_id) {
                    commit('CLEAR_QR_CODE');
                }
                console.log(`WhatsApp готов для messenger_id=${messenger_id}`);
            });

            socket.on('wa-auth-checking', () => {
                commit('SET_WA_AUTH_CHECK_IN_PROGRESS', true);
            });

            socket.on('wa-auth-checked', ({ messenger_id, is_ready }) => {
                commit('SET_WHATSAPP_READY', { messenger_id, status: is_ready });
                commit('SET_WA_AUTH_CHECK_IN_PROGRESS', false);
            });

            socket.on('new-message', (msg) => {
                console.log(`Новое сообщение:`, msg);

                const currentDialogId = state.selectedDialog?.id;

                if (!msg || !msg.dialog_id) {
                    console.warn('Пропущено сообщение без dialog_id', msg);
                    this.dispatch('messengers/fetchDialogs', null, { root: true });
                    return;
                }

                if (!currentDialogId || String(currentDialogId) !== String(msg.dialog_id)) {
                    console.log(`Новое сообщение в другом диалоге dialog_id=${msg.dialog_id}`);

                    // Создаём уведомление
                    const dialogId = msg.dialog_id;
                    const clientName = msg.sender_name || 'Новый клиент';
                    const text = msg.content || 'Новое сообщение';

                    ElNotification({
                      title: `Новое сообщение от ${clientName}`,
                      message: h(
                        'div',
                        {
                          style: {
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                          },
                        },
                        [
                          // Аватар или инициалы, как в el-avatar
                          (() => {
                            let avatarSrc = null;
                            if (msg.avatar) avatarSrc = msg.avatar;
                            else if (msg.gender === 1) avatarSrc = '/img/avatar-male.svg';
                            else if (msg.gender === 2) avatarSrc = '/img/avatar-female.svg';
                            else if (clientName) avatarSrc = null; // инициалы
                            else avatarSrc = '/img/avatar-male.svg';

                            if (avatarSrc) {
                              return h('img', {
                                src: avatarSrc,
                                style: {
                                  width: '32px',
                                  height: '32px',
                                  borderRadius: '50%',
                                  objectFit: 'cover',
                                },
                              });
                            }

                            const initials = clientName
                              ? clientName
                                  .split(' ')
                                  .map(w => w[0])
                                  .join('')
                                  .slice(0, 2)
                                  .toUpperCase()
                              : '?';

                            return h(
                              'div',
                              {
                                style: {
                                  width: '32px',
                                  height: '32px',
                                  borderRadius: '50%',
                                  backgroundColor: 'var(--ui-color-border-default)',
                                  color: 'var(--ui-color-text-strong)',
                                  fontWeight: '600',
                                  fontSize: '13px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  textTransform: 'uppercase',
                                },
                              },
                              initials
                            );
                          })(),
                          h('span', text),
                        ]
                      ),
                      duration: 5000,
                      customClass: 'dialog-incoming-notification',
                      onClick: () => {
                        window.location.href = `/dialogs?dialog_id=${dialogId}`;
                      },
                    });
                    this.dispatch('messengers/fetchDialogs', null, { root: true });
                    return;
                }

                if (String(currentDialogId) === String(msg.dialog_id)) {
                    console.log(`Сообщение добавлено в активный диалог dialog_id=${msg.dialog_id}`, msg);
                    commit('messengers/ADD_MESSAGE', msg, { root: true });
                } else {
                    console.log(`Сообщение получено для dialog_id=${msg.dialog_id}, но сейчас открыт dialog_id=${currentDialogId}, не добавляем`);
                }

            });

            const notificationChannel = `notify_${accountId}_${userId}`;
            socket.on(notificationChannel, (note) => {
                commit('ADD_NOTIFICATION', note);
                console.log(`Уведомление:`, note);
            });

            socket.on('whatsapp-number', async (data) => {
                console.log('Данные номера WhatsApp:', data);
                try {
                    const response = await apiClient('messengers.updateNumber', data);
                    console.log('Номер WhatsApp сохранён:', response);
                    commit('SET_WHATSAPP_NUMBER', data);
                } catch (error) {
                    console.error('Ошибка сохранения номера WhatsApp:', error);
                }
            });

            socket.on('whatsapp-logged-out', ({ messenger_id }) => {
                commit('SET_WHATSAPP_READY', { messenger_id, status: false });
                if (state.currentMessengerId === messenger_id) {
                    commit('CLEAR_QR_CODE');
                }
                commit('SET_WAS_LOGGED_OUT', true);
                console.log(`WhatsApp отключен messenger_id=${messenger_id}`);
            });

            socket.on('messenger-authorized', ({ messenger_id }) => {
                console.log(`Мессенджер авторизован: messenger_id=${messenger_id}`);
                if (state.currentMessengerId === messenger_id) {
                    commit('CLEAR_QR_CODE');
                    commit('SET_CURRENT_MESSENGER_ID', null);
                }
                commit('SET_WHATSAPP_READY', { messenger_id, status: true });
            });

            socket.on('telegram:auth-state', payload => {
                eventBus.emit('telegram:auth-state', payload);
            });
        },
        send({ state }, { event, data, callback }) {
            if (state.socket && state.connected) {
                if (event === 'whatsapp-start-auth' && data.messenger_id) {
                    this.commit('socket/SET_CURRENT_MESSENGER_ID', data.messenger_id);
                }

                if (typeof callback === 'function') {
                    state.socket.emit(event, data, callback);
                } else {
                    state.socket.emit(event, data);
                }
            } else {
                console.error('Socket is not connected, queueing emit (if allowed):', event);

                if (!ALLOWED_QUEUE_EVENTS.has(event)) {
                    // do not queue non-idempotent/heavy events
                    if (typeof callback === 'function') {
                        callback({ success: false, error: 'Socket not connected' });
                    }
                    return;
                }
                // enforce limit
                if (state.pendingEmits.length >= QUEUE_LIMIT) {
                    // drop the oldest
                    state.pendingEmits.shift();
                }
                this.commit('socket/PUSH_PENDING_EMIT', { event, data, callback, timestamp: Date.now() });
            }
        },
    },
};
